import { faChevronDown } from '@fortawesome/pro-light-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-light-svg-icons/faChevronUp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Typography, useMediaQuery } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { status as AppointmentStatusIds } from '@spike/appointment-model';
import {
    AppointmentsStatus,
    BookingsStatus,
    cancelAppointmentThunk,
    cancelRecurrentAppointmentsThunk,
    changeStatusAppointmentThunk,
    completeAppointmentThunk,
    confirmAppointmentThunk
} from '@spike/bookings-action';
import { Option, OWNER_ID } from '@spike/model';
import {
    AppointmentEditOrigin,
    INITIAL_BOOKING_EDIT_SCOPE_REQUEST,
    NewBookingsStatus,
    updateAppointmentThunk
} from '@spike/new-bookings-action';
import { OrdersStatus, reloadOrderThunk } from '@spike/orders-action';
import { TasksStatus } from '@spike/tasks-action';
import useNonInitialEffect from '@versiondos/hooks';
import clsx from 'clsx';
import { CompleteAppointmentDialog } from 'components/UI';
import PopUpCancelAppointment from 'components/UI/PopUpCanceAppointment';
import { reasonNoShowId } from 'components/UI/PopUpCancelBooking';
import { useApiClientWrapper, useAuth, useMasterData, usePermissions, useTimeZone } from 'hooks';
import useHideHubSpotChat from 'hooks/useHideHubSpotChat';
import { NO_SHOW_CANCEL_REASON_ID } from 'model';
import moment, { Moment } from 'moment-timezone';
import { FunctionComponent, MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PulseLoader from 'react-spinners/PulseLoader';
import { RootState } from 'store';
import List from './List';
import { TrainingType, WalkingType } from '@spike/appointments-list-model';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import {
    appendMissingClosedTransitionActions,
    filterAllowedStatusActions,
    isClosedStatus
} from 'utils/appointmentTransitionPolicy';
import { isSessionStatusArea } from 'utils/sessionStatusAreas';

export interface StatusButtonProps {
    appointmentId: number;
    apppointmentMarketplaceId: number;
    recurringUuid?: string;
    inRecurringSeries?: boolean;
    editScopeOrigin?: AppointmentEditOrigin;
    appointmentStatus: Option<number>;
    appointmentClosedAt?: Moment;
    appointmentCancelationReasonId: number | null;
    appointmentTrainingType?: TrainingType;
    appointmentWalkingType?: WalkingType;
    customerFirstName: string;
    customerLastName: string;
    businessAreaId?: string;
    reloadOrderAfterStatusChange: boolean;
    disabled?: boolean;
    fullWidth?: boolean;
    className?: string;
    scrollerContainerId?: string;
    position?: 'fixed' | 'absolute';
    onStartSaving?: () => void;
    onStopSaving?: () => void;
    hasEvaluationResult?: boolean;
    onEvaluationResultRequired?: (statusId: number) => void;
}

interface ButtonColors {
    color: string;
    backgroundColor: string;
    borderColor: string;
}

const bookedColor = { color: '#EAB464', backgroundColor: '#FFF', borderColor: '#EAB464' };
const confirmedColor = { color: '#FFF', backgroundColor: '#EAB464', borderColor: '#EAB464' };
const checkedInColor = { color: '#FFF', backgroundColor: '#BDD2CA', borderColor: '#BDD2CA' };
const inProgressColor = { color: '#FFF', backgroundColor: '#92B4A7', borderColor: '#92B4A7' };
const completedColor = { color: '#FFF', backgroundColor: '#5E8677', borderColor: '#5E8677' };
const defaultColor = { color: '#FFF', backgroundColor: '#BCB8AE', borderColor: '#BCB8AE' };

const STATUS_OVERLAY_Z_INDEX = 10200;

// The ready for pick-up text is salon specific copy: the pet parent drops the pet off and comes
// back for it. Every other service category completes without offering the message.
const READY_FOR_PICK_UP_BUSINESS_AREAS = ['grooming', 'dental'];

const colors = new Map<number, ButtonColors>([
    [AppointmentStatusIds.BOOKED, bookedColor],
    [AppointmentStatusIds.CONFIRMED, confirmedColor],
    [AppointmentStatusIds.CHECKED_IN, checkedInColor],
    [AppointmentStatusIds.IN_PROGRESS, inProgressColor],
    [AppointmentStatusIds.COMPLETED, completedColor],
    [AppointmentStatusIds.READY_FOR_CHECK_OUT, completedColor],
    [AppointmentStatusIds.PAYMENT, completedColor]
]);

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            position: 'relative',
            display: 'flex',
            flexDirection: 'column'
        },
        button: (buttonColor: ButtonColors) => ({
            display: 'flex',
            justifyContent: 'space-between',
            cursor: 'pointer',
            alignItems: 'center',
            padding: '9px 16px',
            borderWidth: '2px',
            borderStyle: 'solid',
            borderColor: buttonColor.borderColor,
            borderRadius: 50,
            color: buttonColor.color,
            background: buttonColor.backgroundColor
        }),
        notClickable: {
            cursor: 'default !important',
            opacity: 0.5
        },
        buttonLabel: {
            fontSize: 16,
            fontWeight: 600,
            lineHeight: '100%',
            textAlign: 'center',
            width: '100%'
        },
        chevronIcon: {
            marginLeft: 10
        },
        spinnerContainer: (buttonColor: ButtonColors) => ({
            position: 'absolute',
            top: 0,
            left: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            paddingBottom: 5,
            borderWidth: '2px',
            borderStyle: 'solid',
            borderColor: buttonColor.borderColor,
            borderRadius: 50,
            color: buttonColor.color,
            background: buttonColor.backgroundColor
        })
    })
);

const getButtonLabel = (
    status: Option<number>,
    appointmentCancelationReasonId: number | null,
    businessAreaId?: string
) => {
    switch (status.id) {
        case AppointmentStatusIds.PAYMENT:
            return 'Completed';
        case AppointmentStatusIds.CANCELLED:
            return appointmentCancelationReasonId === NO_SHOW_CANCEL_REASON_ID ? 'No Show' : status.name;
        default:
            return status.name;
    }
};

export const StatusButton: FunctionComponent<StatusButtonProps> = props => {
    const color = colors.get(props.appointmentStatus.id) ?? defaultColor;

    const classes = useStyles(color);

    const masterData = useMasterData();
    const dispatch = useDispatch();
    const auth = useAuth();
    const permissions = usePermissions();
    const timezone = useTimeZone();
    const { isUserLoggedInMarketplace } = useOrganizationMarketplaces();
    const apiClientWrapper = useApiClientWrapper();
    const isLg = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));
    const isMobile = useMediaQuery('(max-width:600px)');

    const allowActions = isUserLoggedInMarketplace(props.apppointmentMarketplaceId);
    const containerRef = useRef<HTMLDivElement>(null);

    const workflow = useMemo(() => {
        if (props.businessAreaId === 'daycare') {
            return masterData.workflows.daycare;
        } else if (props.businessAreaId === 'boarding') {
            return masterData.workflows.boarding;
        } else if (props.businessAreaId === 'transportation') {
            return masterData.workflows.transportation;
        } else if (props.businessAreaId === 'training') {
            if (props.appointmentTrainingType === 'in_home') {
                return masterData.workflows.training.inHome;
            } else if (props.appointmentTrainingType === 'brick_and_mortar') {
                return masterData.workflows.training.brickAndMortar;
            } else {
                return [];
            }
        } else if (props.businessAreaId === 'walking') {
            if (props.appointmentWalkingType === 'in_home') {
                return masterData.workflows.walking.inHome;
            } else if (props.appointmentWalkingType === 'brick_and_mortar') {
                return masterData.workflows.walking.brickAndMortar;
            } else {
                return [];
            }
        } else {
            return masterData.workflows.grooming;
        }
    }, [props.businessAreaId, props.appointmentTrainingType, props.appointmentWalkingType, masterData]);

    const appointmentStatus = useSelector<RootState, AppointmentsStatus>(state => state.appointments.status);
    const bookingStatus = useSelector<RootState, BookingsStatus>(state => state.bookings.status);
    const ordersStatus = useSelector<RootState, OrdersStatus>(state => state.orders.status);
    const tasksStatus = useSelector<RootState, TasksStatus>(state => state.tasks.status);
    const newBookingsStatus = useSelector<RootState, NewBookingsStatus>(state => state.newBookings.status);

    const [expanded, setExpanded] = useState(false);

    const [saving, setSaving] = useState(false);
    const scopedCancelInFlight = useRef(false);
    const [showModal, setShowModal] = useState({ cancel: false, complete: false, noShow: false });

    const [coords, setCoords] = useState({ x: 0, y: 0 });

    // Hide HubSpot chat when mobile options are expanded
    useHideHubSpotChat(
        (expanded && !isLg) ||
            (showModal.cancel && !isLg) ||
            (showModal.complete && !isLg) ||
            (showModal.noShow && !isLg)
    );

    const updateCoords = () => {
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            setCoords({
                x: rect.x,
                y: rect.y
            });
        }
    };

    useEffect(() => {
        if (containerRef.current) {
            updateCoords();
        }
    }, [containerRef]);

    useEffect(() => {
        if (props.scrollerContainerId) {
            const listContainer = document.querySelector(`#${props.scrollerContainerId}`);

            listContainer && listContainer.addEventListener('scroll', updateCoords);
            window.addEventListener('resize', updateCoords);

            return () => {
                listContainer && listContainer.removeEventListener('scroll', updateCoords);
                window.removeEventListener('resize', updateCoords);
            };
        }
    }, []);

    useEffect(() => {
        if (!isMobile) {
            const handleClickOutside = (event: MouseEvent | TouchEvent) => {
                if (expanded && containerRef.current && !containerRef.current.contains(event.target as Node)) {
                    setExpanded(false);
                }
            };

            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);

            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
                document.removeEventListener('touchstart', handleClickOutside);
            };
        }
    }, [expanded]);

    useNonInitialEffect(() => {
        switch (ordersStatus) {
            case OrdersStatus.ReloadSuccess:
            case OrdersStatus.Error:
                setSaving(false);
                props.onStopSaving && props.onStopSaving();
        }
    }, [ordersStatus]);

    useNonInitialEffect(() => {
        if (saving) {
            switch (appointmentStatus) {
                case AppointmentsStatus.ChangeStatusSuccess:
                case AppointmentsStatus.CompleteSuccess:
                    if (props.reloadOrderAfterStatusChange) {
                        dispatch(reloadOrderThunk(apiClientWrapper));
                    } else {
                        setSaving(false);
                        props.onStopSaving?.();
                    }
                    break;
                case AppointmentsStatus.Error:
                    setSaving(false);
                    props.onStopSaving?.();
            }
        }
    }, [appointmentStatus]);

    useNonInitialEffect(() => {
        if (saving) {
            switch (bookingStatus) {
                case BookingsStatus.ConfirmAppointmentSuccess:
                    if (props.reloadOrderAfterStatusChange) {
                        dispatch(reloadOrderThunk(apiClientWrapper));
                    } else {
                        setSaving(false);
                        props.onStopSaving?.();
                    }
                    break;
                case BookingsStatus.Error:
                    setSaving(false);
                    props.onStopSaving?.();
            }
        }
    }, [bookingStatus]);

    useNonInitialEffect(() => {
        if (scopedCancelInFlight.current) {
            switch (newBookingsStatus) {
                case NewBookingsStatus.UpdateAppointmentScopeRequired:
                case NewBookingsStatus.UpdateAppointmentScopeCancelled:
                case NewBookingsStatus.UpdateAppointmentSuccess:
                case NewBookingsStatus.Error:
                    scopedCancelInFlight.current = false;
                    setSaving(false);
                    props.onStopSaving?.();
            }
        }
    }, [newBookingsStatus]);

    useNonInitialEffect(() => {
        switch (tasksStatus) {
            case TasksStatus.BulkCompleteSuccess:
                if (props.reloadOrderAfterStatusChange) {
                    dispatch(reloadOrderThunk(apiClientWrapper));
                }
                break;
        }
    }, [tasksStatus]);

    const actualStatus = useMemo(
        () => workflow.find(step => step.status.id === props.appointmentStatus.id),
        [props.appointmentStatus, workflow]
    );

    const nowInTimezone = useMemo(() => moment.tz(timezone), [timezone]);

    const transitionContext = useMemo(
        () => ({
            currentStatusId: props.appointmentStatus.id,
            isOwner: auth.user?.role?.id === OWNER_ID,
            reopenClosedBookings: permissions.checkout.reopenClosedBookings,
            closedAt: props.appointmentClosedAt,
            now: nowInTimezone
        }),
        [
            auth.user?.role?.id,
            permissions.checkout.reopenClosedBookings,
            props.appointmentStatus.id,
            props.appointmentClosedAt,
            nowInTimezone
        ]
    );

    const actions = useMemo(() => {
        const workflowActions = actualStatus?.actions.filter(action => action.show) ?? [];
        const filtered = filterAllowedStatusActions(workflowActions, transitionContext);
        return appendMissingClosedTransitionActions(filtered, transitionContext, masterData.status);
    }, [actualStatus, transitionContext, masterData.status]);

    const hasActions = actions.length > 0;

    const buttonLabel = useMemo(
        () => getButtonLabel(props.appointmentStatus, props.appointmentCancelationReasonId, props.businessAreaId),
        [props.appointmentStatus, props.appointmentCancelationReasonId, props.businessAreaId]
    );

    const showReadyForPickUpOption = READY_FOR_PICK_UP_BUSINESS_AREAS.includes(props.businessAreaId ?? '');

    const changeStatus = (statusId: number) => {
        if (
            props.businessAreaId === 'evaluation' &&
            [AppointmentStatusIds.READY_FOR_CHECK_OUT, AppointmentStatusIds.COMPLETED].includes(statusId) &&
            !props.hasEvaluationResult &&
            props.onEvaluationResultRequired
        ) {
            props.onEvaluationResultRequired(statusId);
        } else if (statusId === AppointmentStatusIds.CONFIRMED && !isSessionStatusArea(props.businessAreaId)) {
            // Grooming "Confirmed" goes through the confirm endpoint (sends the confirmation email).
            // Session areas use status 2 as "On My Way", a plain status update handled below.
            setSaving(true);
            props.onStartSaving && props.onStartSaving();
            props.appointmentId && dispatch(confirmAppointmentThunk(apiClientWrapper, props.appointmentId));
        } else if (statusId === AppointmentStatusIds.CANCELLED) {
            setShowModal(prev => ({ ...prev, cancel: true }));
        } else if (statusId === AppointmentStatusIds.NO_SHOW) {
            setShowModal(prev => ({ ...prev, noShow: true }));
            /*
            setSaving(true);
            props.onStartSaving && props.onStartSaving();
            props.appointment.id &&
                dispatch(cancelAppointmentThunk(apiClientWrapper, props.appointment.id, false, reasonNoShowId));*/
        } else if (
            statusId === AppointmentStatusIds.READY_FOR_CHECK_OUT &&
            !isClosedStatus(props.appointmentStatus.id)
        ) {
            setShowModal(prev => ({ ...prev, complete: true }));
        } else {
            setSaving(true);
            props.onStartSaving && props.onStartSaving();
            props.appointmentId &&
                dispatch(changeStatusAppointmentThunk(apiClientWrapper, props.appointmentId, statusId));
        }
    };

    const clickHandler = (statusId: number) => {
        if (allowActions) {
            setExpanded(false);
            changeStatus(statusId);
        }
    };

    const closeCancelationHandler = () => {
        setShowModal(prev => ({ ...prev, cancel: false, noShow: false }));
    };

    const confirmCancelHandler = (appointmentId?: number, sendNotification?: boolean, cancelationReasonId?: number) => {
        if (appointmentId && props.inRecurringSeries) {
            setSaving(true);
            props.onStartSaving && props.onStartSaving();
            closeCancelationHandler();
            scopedCancelInFlight.current = true;
            dispatch(
                updateAppointmentThunk(
                    apiClientWrapper,
                    appointmentId,
                    { type: 'cancel', statusId: AppointmentStatusIds.CANCELLED, cancelationReasonId },
                    sendNotification,
                    props.editScopeOrigin ?? 'order_detail',
                    INITIAL_BOOKING_EDIT_SCOPE_REQUEST
                )
            );
        } else if (appointmentId) {
            setSaving(true);
            props.onStartSaving && props.onStartSaving();
            closeCancelationHandler();
            dispatch(cancelAppointmentThunk(apiClientWrapper, appointmentId!, sendNotification, cancelationReasonId));
        }
    };

    const confirmCancelRecurrentHandler = (
        appointmentId?: number,
        sendNotification?: boolean,
        cancelationReasonId?: number
    ) => {
        if (appointmentId) {
            setSaving(true);
            props.onStartSaving && props.onStartSaving();
            closeCancelationHandler();
            dispatch(
                cancelRecurrentAppointmentsThunk(
                    apiClientWrapper,
                    appointmentId!,
                    AppointmentStatusIds.CANCELLED,
                    sendNotification,
                    cancelationReasonId
                )
            );
        }
    };

    const completeHandler = (appointmentId: number, sendNotification: boolean) => {
        closeCompleteModal();
        setSaving(true);
        props.onStartSaving && props.onStartSaving();
        props.appointmentId && dispatch(completeAppointmentThunk(apiClientWrapper, appointmentId, sendNotification));
    };

    const closeCompleteModal = () => {
        setShowModal(prev => ({ ...prev, complete: false }));
    };

    const clickButtonHandler = (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();

        if (allowActions) {
            if (!saving && !props.disabled) {
                updateCoords();
                setExpanded(prev => !prev);
            }
        }
    };

    const spinner = (
        <Box className={classes.spinnerContainer}>
            <PulseLoader
                color={color.color}
                size={5}
            />
        </Box>
    );

    return (
        <>
            <div
                ref={containerRef}
                className={clsx(classes.container, props.className)}
                style={props.fullWidth ? { minWidth: '100%' } : undefined}
            >
                {saving && spinner}
                <Box
                    className={clsx(classes.button, { [classes.notClickable]: !allowActions || props.disabled })}
                    onClick={clickButtonHandler}
                >
                    <Typography className={classes.buttonLabel}>{buttonLabel}</Typography>
                    {allowActions && !props.disabled && hasActions && (
                        <FontAwesomeIcon
                            icon={expanded ? faChevronUp : faChevronDown}
                            className={classes.chevronIcon}
                        />
                    )}
                </Box>
                {expanded && allowActions && !props.disabled && hasActions && !saving && (
                    <List
                        actions={actions}
                        x={coords.x}
                        y={coords.y}
                        position={props.position ?? 'absolute'}
                        onClick={clickHandler}
                        onClose={() => setExpanded(false)}
                    />
                )}
            </div>
            {(showModal.cancel || showModal.noShow) && (
                <PopUpCancelAppointment
                    loading={saving}
                    appointmentId={props.appointmentId}
                    recurringUuid={props.inRecurringSeries ? undefined : props.recurringUuid}
                    customerFirstName={props.customerFirstName}
                    customerLastName={props.customerLastName}
                    initCancelationReasonId={showModal.noShow ? reasonNoShowId : undefined}
                    initSendNotification={showModal.cancel}
                    onClose={closeCancelationHandler}
                    onConfirmCancel={confirmCancelHandler}
                    onConfirmCancelRecurrent={confirmCancelRecurrentHandler}
                    zIndex={STATUS_OVERLAY_Z_INDEX}
                />
            )}
            {showModal.complete && (
                <CompleteAppointmentDialog
                    zIndex={STATUS_OVERLAY_Z_INDEX}
                    showSendSmsOption={showReadyForPickUpOption}
                    onCancel={closeCompleteModal}
                    onComplete={(sendNotification: boolean) =>
                        props.appointmentId && completeHandler(props.appointmentId, sendNotification)
                    }
                />
            )}
        </>
    );
};

export default StatusButton;
